import React, {forwardRef, useImperativeHandle} from "react";
import {useImageCropper} from "../hooks/use-image-cropper";
import {useCropInteractions} from "../hooks/use-crop-interactions";
import {useWheelZoom} from "../hooks/use-wheel-zoom";
import CropOverlay from "./ui/crop-overlay";
import SpinnerLoader from "@common/components/spinner-loader";

export interface ImageCropperHandle {
    getCroppedBlob: () => Promise<Blob>;
    isReady: boolean;
    zoom: number;
    setZoom: (delta: number) => void;
}

interface ImageCropperProps {
    imageUrl: string;
    aspect?: number;
    cropShape?: 'rect' | 'round';
}

const ImageCropper = forwardRef<ImageCropperHandle, ImageCropperProps>((props, ref) => {
    const {imageUrl, aspect = 1, cropShape = 'rect'} = props;

    const {
        isImageLoaded, containerSize, displayWidth, displayHeight, imageOffset, cropArea,
        containerRef, handleZoom, moveCrop, resizeCrop, getCroppedBlob, zoom
    } = useImageCropper({imageUrl, aspect});

    const {onMouseMove, onCropMouseDown, onResizeMouseDown, onMouseUp} = useCropInteractions({moveCrop, resizeCrop});

    useWheelZoom(containerRef, handleZoom);

    useImperativeHandle(ref, () => ({
        getCroppedBlob,
        isReady: isImageLoaded,
        zoom,
        setZoom: handleZoom,
    }), [getCroppedBlob, isImageLoaded, zoom, handleZoom]);

    if (!isImageLoaded) {
        return (
            <div className="flex-1 flex justify-center items-center" style={{minHeight: '300px'}}>
                <SpinnerLoader color="#333"/>
            </div>
        );
    }

    return (
        <div className="sm:px-8 px-6 sm:pt-4 sm:pb-6 pt-2 pb-3 flex-1 flex justify-center">
            <div
                ref={containerRef}
                className="relative bg-gray-900 rounded-lg overflow-hidden"
                style={{width: containerSize.width, height: containerSize.height}}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
            >
                <img
                    src={imageUrl}
                    alt="Crop"
                    className="absolute select-none"
                    style={{
                        width: displayWidth,
                        height: displayHeight,
                        maxWidth: 'none',
                        pointerEvents: 'none',
                        left: imageOffset.x,
                        top: imageOffset.y,
                    }}
                    draggable={false}
                />
                <CropOverlay
                    cropArea={cropArea}
                    cropShape={cropShape}
                    onCropMouseDown={onCropMouseDown}
                    onResizeMouseDown={onResizeMouseDown}
                />
            </div>
        </div>
    );
});

ImageCropper.displayName = 'ImageCropper';

export default ImageCropper;
